<template>
  <InfuseModal
    :title="
      isUpdate ? t('iam.organisations.header.update_organisation') : t('iam.organisations.header.create_organisation')
    "
    @close="emit('close')"
    :disable-close="loading"
  >
    <form :id="formId" @submit.prevent="onSubmit" class="flex flex-col gap-2">
      <InfuseField :label="t('iam.organisations.name')">
        <InfuseInput
          :required="true"
          v-model="nameMutation"
          ref="nameInputRef"
          :placeholder="t('iam.organisations.name_placeholder')"
        />
      </InfuseField>

      <InfuseField :label="t('iam.organisations.description')">
        <InfuseInput
          :required="false"
          v-model="descriptionMutation"
          ref="descriptionInputRef"
          :placeholder="t('iam.organisations.description_placeholder')"
        />
      </InfuseField>
    </form>
    <template #actions>
      <InfuseButton color="secondary" type="button" @click="emit('close')" :disabled="loading">
        {{ t('action.cancel') }}
      </InfuseButton>

      <InfuseButton type="submit" :form="formId" :disabled="loading" :loading="loading">
        {{ t('action.submit') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import { InfuseButton, InfuseField, InfuseInput, InfuseModal } from '@infuse/ui/components';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { CreateOrganisation, Organisation, UpdateOrganisation } from '@infuse/api/iam/organisation';

const { t } = useI18n();

const nameMutation = ref('');
const descriptionMutation = ref<string | undefined>('');

const isUpdate = computed(() => !!props.organisation);
const formId = computed(() => (isUpdate.value ? 'update-organisation' : 'add-organisation'));

const props = withDefaults(
  defineProps<{
    loading?: boolean;
    organisation?: Organisation;
  }>(),
  {
    loading: false
  }
);

interface Emits {
  (event: 'close'): void;
  (event: 'mutate', vars: { id: string | undefined; organisation: CreateOrganisation | UpdateOrganisation }): void;
}

const emit = defineEmits<Emits>();

watch(
  () => props.organisation,
  (organisation) => {
    if (!organisation) {
      return;
    }
    nameMutation.value = organisation.name;
    descriptionMutation.value = organisation.description;
  },
  { immediate: true }
);

const onSubmit = () => {
  emit('mutate', {
    id: props.organisation?.id,
    organisation: {
      name: nameMutation.value,
      description: descriptionMutation.value
    }
  });
};
</script>
